
// Import React
import React, { useState, useEffect, useRef } from 'react';

// Generic imports
import _ from 'lodash';
import { arrayMoveImmutable as arrayMove } from 'array-move';

// Import MUI
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import Alert from '@mui/material/Alert';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons';

// Local imports
import Editor from '../opportunities/freetext/OpportunityOneOffFreeTextBuilder.editor';

function TermsSelector(props) {
    const { opportunity, organization, onUpdateOpportunity } = props;
    const theme = useTheme();

    const orgTerms = organization.terms;
    const terms = opportunity.terms;
    const transportationMethod = opportunity.transportationMethod;

    function setTerms(newTerms) {
        onUpdateOpportunity({ opportunity: { ...opportunity, terms: newTerms } });
    }

    const [templateSelection, setTemplateSelection] = useState('');
    const [editing, setEditing] = useState(null);

    // Only auto-populate defaults if terms is null/undefined (never been set), not if it's an empty array (explicitly cleared)
    useEffect(() => {
        // If terms is explicitly set (even if empty array), don't auto-populate
        if (terms !== null && terms !== undefined) return;
        // Only auto-populate if terms is null/undefined (never been set)
        if (!orgTerms) return;
        if (orgTerms[`price-quote-${transportationMethod}`]) {
            setTerms([{ name: `price-quote-${transportationMethod}`, value: orgTerms[`price-quote-${transportationMethod}`] }]);
        } else if (orgTerms['price-quote']) {
            setTerms([{ name: 'price-quote', value: orgTerms['price-quote'] }]);
        } else {
            setTerms([]);
        }
    }, [orgTerms, transportationMethod]);

    function addBlank() {
        // If terms is null/undefined, initialize as empty array first
        const currentTerms = (terms !== null && terms !== undefined) ? terms : [];
        setTerms([...currentTerms, { name: 'Custom', value: '' }]);
    }

    function addTemplate(template) {
        // If terms is null/undefined, initialize as empty array first
        const currentTerms = (terms !== null && terms !== undefined) ? terms : [];
        setTerms([...currentTerms, { name: template, value: orgTerms[template] }]);
    }

    function moveItemUp(index) {
        if (index === 0) return;
        const currentTerms = (terms !== null && terms !== undefined) ? terms : [];
        setTerms(arrayMove(currentTerms, index, index - 1));
    }

    function moveItemDown(index) {
        const currentTerms = (terms !== null && terms !== undefined) ? terms : [];
        if (index === currentTerms.length - 1) return;
        setTerms(arrayMove(currentTerms, index, index + 1));
    }

    return (
        <Box sx={{ background: theme.palette.background.default, width: '100%' }}>
            <Box sx={{ background: theme.palette.primary.main, padding: '6px 24px', mb: 3, color: theme.palette.primary.contrast, boxShadow: '0px -1px 1px rgba(0,0,0,0.1)' }}>
                <Typography variant='body1' sx={{ color: theme.palette.primary.contrastText }}>
                    Terms & Conditions
                </Typography>
            </Box>

            <Box sx={{ marginTop: '36px', padding: '24px', maxWidth: 'calc(100% - 48px)', width: 'calc(100% - 48px)', margin: 'auto', padding: '24px 12px', border: `1px solid ${theme.palette.border1}`, borderRadius: '6px', background: theme.palette.panel1 }}>
                {(terms !== null && terms !== undefined ? terms : []).map((term, index) => (
                    <MenuItem key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }} onClick={() => setEditing(index)}>
                        <Typography variant='body2' sx={{ width: '200px' }}>Chapter {index+1}: {idToText(term.name)}</Typography>
                        <IconButton color='primary' onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            moveItemUp(index);
                        }} size='small' disabled={index === 0}>
                            <ArrowDropUpIcon />
                        </IconButton>
                        <IconButton color='primary' onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            moveItemDown(index);
                        }} size='small' disabled={index === terms.length - 1}>
                            <ArrowDropDownIcon />
                        </IconButton>
                        <IconButton color='error' sx={{ marginLeft: '24px' }} onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            const currentTerms = (terms !== null && terms !== undefined) ? terms : [];
                            const newTerms = currentTerms.filter((t, i) => i !== index);
                            // If all terms are deleted, set to empty array (explicitly cleared)
                            setTerms(newTerms.length === 0 ? [] : newTerms);
                        }}>
                            <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                    </MenuItem>
                ))}

                <Box sx={{ display: 'flex', mt: (terms !== null && terms !== undefined ? terms : []).length > 0 ? 2 : 0 }}>
                    <FormControl sx={{ width: '250px', mr: 2 }}>
                        <InputLabel id='pick-template-label'>Load a template</InputLabel>
                        <Select
                            labelId='pick-template-label'
                            id='pick-template'
                            label='Load a template'
                            sx={{ flex: 1 }}
                            value={templateSelection}
                            onChange={e => setTemplateSelection(e.target.value)}
                            MenuProps={{
                                style: { zIndex: 1000001 }
                            }}
                        >
                            {_.keys(orgTerms).filter(k => !!orgTerms[k]).map((key, index) => (
                                <MenuItem key={key} value={key}>{idToText(key)}</MenuItem>
                            ))}
                            <Divider />
                            <MenuItem value='scratch'><i>Start from scratch</i></MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        disabled={!templateSelection} 
                        variant='contained' 
                        sx={{ p: 2 }} 
                        onClick={() => templateSelection === 'scratch' ? addBlank() : addTemplate(templateSelection)}
                    >
                        + Chapter
                    </Button>
                </Box>
                {editing !== null && (
                    <EditDialog
                        open={true}
                        onClose={() => setEditing(null)}
                        value={terms[editing]?.value}
                        onChange={v => {
                            const currentTerms = (terms !== null && terms !== undefined) ? terms : [];
                            const newTerms = [...currentTerms];
                            newTerms[editing] = { ...(newTerms[editing] || {}), value: v };
                            setTerms(newTerms);
                        }}
                    />
                )}
            </Box>
        </Box>
    )
}

// The rich text editor only understands the markup it has nodes for. A chapter holding
// raw HTML it cannot model - a <style> block is the case that bit us - parses to an
// entirely empty document, and its serialization is then an empty document too. Saving
// that back over the chapter wipes it, which is what "clicking a chapter deletes it"
// actually was.
function htmlToText(html) {
    return (html || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .trim();
}

// True when the editor handed back nothing but the chapter did have content: the editor
// failed to load it, rather than the user having cleared it.
function wouldWipeContent({ next, original }) {
    return htmlToText(next) === '' && htmlToText(original) !== '';
}

function EditDialog(props) {
    const { open, onClose, value, onChange } = props;
    const [localValue, setLocalValue] = useState(value);
    const editorRef = useRef(null);

    // Nothing in the editor's schema can represent these, so they never survive a round
    // trip - warn rather than let someone lose the chapter by opening it.
    const notEditableHere = /<\s*(style|script)[\s>]/i.test(value || '');

    function onBlur({ html }) {
        setLocalValue(html);
    }

    function onSave() {
        onChange(wouldWipeContent({ next: localValue, original: value }) ? value : localValue);
        onClose();
    }

    return (
        <Dialog maxWidth='lg' fullWidth open={true} onClose={onClose} sx={{ zIndex: 1000001 }}>
            <DialogTitle>Edit Terms & Conditions</DialogTitle>
            <DialogContent>
                {notEditableHere && (
                    <Alert severity='warning' sx={{ mb: 2 }}>
                        This chapter contains raw HTML (a <b>&lt;style&gt;</b> or <b>&lt;script&gt;</b> block) that this
                        editor cannot show, so it appears empty here. Its content is kept as it is —
                        edit it under Settings → Terms &amp; Conditions instead.
                    </Alert>
                )}
                <Editor
                    ref={editorRef}
                    sx={{ paddingBottom: '0px' }}
                    initialContent={localValue}
                    onBlur={onBlur}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button onClick={onSave} sx={{ fontFamily: 'Poppins, sans-serif' }}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

const idToText = id => {
    return {
        'price-quote': 'Price Quote',
        'price-quote-air': 'Air Price Quote',
        'price-quote-sea': 'Ocean Price Quote',
        'price-quote-land': 'Land Price Quote',
        'agent-query': 'Agent Query',
    }[id] || id;
};

export default TermsSelector;